import { ActivatedRoute } from '@angular/router'
import { BookingDetails } from '../domain/BookingDetails'
import { BookingDetailsViewModel } from './BookingDetailsViewModel'
import { BookingService } from '../services/booking.service'
import { Component, OnDestroy, OnInit } from '@angular/core'
import {
    Observable,
    Subject,
    catchError,
    combineLatest,
    from,
    map,
    of,
    shareReplay,
    switchMap,
    takeUntil,
    tap,
} from 'rxjs'
import { PaymentsService } from '../services/payments.service'
import { Context } from '../domain/Context'
import { VenueService } from '../services/venue.service'

@Component({
    selector: 'app-booking-summary',
    templateUrl: './booking-summary.component.html',
})
export class BookingSummaryComponent implements OnInit, OnDestroy {

    viewModel: Observable<BookingDetailsViewModel>
    readonly resumePaymentConfirmation: boolean
    private onDestroy$ = new Subject<boolean>()

    constructor(
        route: ActivatedRoute,
        bookingService: BookingService,
        paymentsService: PaymentsService,
        venueService: VenueService
    ) {
        const queryParams = route.snapshot.queryParamMap
        this.resumePaymentConfirmation = queryParams.has('paymentIntentId') || queryParams.has('setupIntentId')
        this.viewModel = combineLatest([route.paramMap, route.queryParamMap]).pipe(
            switchMap(([params, queryParams]) => {
                const bookingId = params.get('bookingId') ?? ''
                const setupBookingId = queryParams.get('setupBookingId')
                return bookingService.getBookingDetails(bookingId).pipe(
                    switchMap(details => {
                        const setupIntentId = queryParams.get('setupIntentId') ?? paymentsService.pendingDojoSetupIntentId()
                        if (setupBookingId !== bookingId || setupIntentId === null) {
                            return of(details)
                        }
                        return venueService.getWithId(details.venueId).pipe(
                            switchMap(venue => bookingService.waitForPaymentConfirmation(
                                new Context(venue.organisationId, venue.businessId, venue.id),
                                { setupIntentId },
                                () => from(paymentsService.confirmSetupIntent(
                                    venue.organisationId, venue.businessId, setupIntentId, { bookingId }
                                ))
                            ).pipe(tap(confirmed => {
                                if (confirmed.bookingId !== bookingId) {
                                    throw new Error('Payment confirmation does not match this booking')
                                }
                                paymentsService.clearPendingDojoSetupIntent()
                            }))),
                            catchError(() => of(details))
                        )
                    }),
                    map(details => this.makeViewModel(details))
                )
            }),
            takeUntil(this.onDestroy$),
            shareReplay({ bufferSize: 1, refCount: true })
        )
    }

    ngOnInit() { }

    ngOnDestroy() {
        this.onDestroy$.next(true)
        this.onDestroy$.complete()
    }

    private makeViewModel(details: BookingDetails): BookingDetailsViewModel {
        return new BookingDetailsViewModel(details, false, false)
    }
}
